import { type ActivityEvent, type ActivityEventType, MAX_EVENTS_PER_BATCH } from "@solution-advisor/context-events";

/** What a caller supplies; the tracker fills in id, time, session and page. */
export type TrackInput<TType extends ActivityEventType = ActivityEventType> =
	Extract<ActivityEvent, { type: TType }> extends infer E
		? E extends ActivityEvent
			? { type: E["type"]; data: E["data"] }
			: never
		: never;

export interface ActivityTransport {
	/** Normal flush. Rejects on failure so the batch can be retried. */
	send(body: string): Promise<void>;
	/** Fire-and-forget flush while the page is going away. */
	beacon(body: string): void;
}

export interface ActivityTrackerOptions {
	transport: ActivityTransport;
	sessionId: string;
	getPage: () => ActivityEvent["page"];
	/** The Company › Project › Solution the user is in right now (set by the page). */
	getScope?: () => ActivityEvent["scope"];
	flushIntervalMs?: number;
	now?: () => Date;
	newId?: () => string;
}

const DEFAULT_FLUSH_INTERVAL_MS = 4_000;
/** Bound memory if the API is unreachable for a long time: oldest events go first. */
const MAX_QUEUE_LENGTH = MAX_EVENTS_PER_BATCH * 4;
/** Enough batches to empty a full queue when every send succeeds. */
const MAX_FLUSH_ATTEMPTS = MAX_QUEUE_LENGTH / MAX_EVENTS_PER_BATCH;

/**
 * Batches activity events and ships them to the API. Every event is stamped with the tab session
 * and the current page, so feature code only says *what* happened.
 */
export const createActivityTracker = ({
	transport,
	sessionId,
	getPage,
	getScope = () => undefined,
	flushIntervalMs = DEFAULT_FLUSH_INTERVAL_MS,
	now = () => new Date(),
	newId = () => crypto.randomUUID()
}: ActivityTrackerOptions) => {
	let queue: ActivityEvent[] = [];
	let sending: Promise<void> | undefined;
	let timer: ReturnType<typeof setTimeout> | undefined;
	/** Bumped by `discard`, so a send that fails afterwards cannot put its batch back. */
	let generation = 0;

	const stopTimer = () => {
		if (timer) clearTimeout(timer);
		timer = undefined;
	};

	const takeBatch = (): ActivityEvent[] => {
		const batch = queue.slice(0, MAX_EVENTS_PER_BATCH);
		queue = queue.slice(batch.length);
		return batch;
	};

	const flush = async (): Promise<void> => {
		stopTimer();
		if (sending) return sending;
		if (queue.length === 0) return;
		const batch = takeBatch();
		const sentIn = generation;
		sending = (async () => {
			try {
				await transport.send(JSON.stringify({ events: batch }));
			} catch {
				if (sentIn === generation) queue = [...batch, ...queue].slice(-MAX_QUEUE_LENGTH);
			} finally {
				sending = undefined;
			}
			if (queue.length > 0) schedule();
		})();
		return sending;
	};

	/** Drops everything queued and stops the timer; a send already under way cannot re-queue its batch. */
	const discard = () => {
		generation += 1;
		queue = [];
		stopTimer();
	};

	const schedule = () => {
		timer ??= setTimeout(() => void flush(), flushIntervalMs);
	};

	return {
		track: (input: TrackInput) => {
			const scope = getScope();
			queue = [
				...queue,
				{
					...input,
					id: newId(),
					occurredAt: now().toISOString(),
					sessionId,
					page: getPage(),
					...(scope ? { scope } : {})
				} as ActivityEvent
			].slice(-MAX_QUEUE_LENGTH);
			if (queue.length >= MAX_EVENTS_PER_BATCH) void flush();
			else schedule();
		},
		flush,
		discard,
		/**
		 * Delivers what is queued under the current session, then drops whatever could not be sent.
		 * Call it before the session cookie changes (sign-out, organization switch): the API stamps
		 * each event with the session it arrives under, so an event tracked in one organization must
		 * never be sent after the switch.
		 */
		settle: async (): Promise<void> => {
			await sending;
			for (let attempt = 0; attempt < MAX_FLUSH_ATTEMPTS && queue.length > 0; attempt += 1) await flush();
			discard();
		},
		/** Called on pagehide / tab hidden / unmount: hand everything to the browser to deliver after unload. */
		flushOnExit: () => {
			stopTimer();
			while (queue.length > 0) transport.beacon(JSON.stringify({ events: takeBatch() }));
		},
		get pending() {
			return queue.length;
		}
	};
};

export type ActivityTracker = ReturnType<typeof createActivityTracker>;
